import { useCallback, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { rootUiActions } from 'app-components/flux/root-ui';
import { securitiesUtils } from 'app-components/flux/securities';
import {
  transactionsActions,
  transactionsSelectors,
  transactionsTypes,
  transactionsUtils,
} from 'app-components/flux/transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import type {
  InvestmentTransactionFormInput,
  InvestmentTransactionFormSubmitPayload,
  investmentTransactionFormFactory,
} from 'app-components/forms/investment/investmentTransactionForm';
import { tracker } from 'app-components/utils/core';
import { toPlainIfImmutable } from 'app-components/utils/core/immutableHelpers';

import useTxnActions from 'data/transactionActions/useTxnActions';
import { useAppDispatch } from 'hooks/redux';
import trackEventTTI from 'utils/trackEventTTI';

import type { InvestmentTransactionEditDialogProps } from '../types';

type InvestmentSymbolLookupItem = NonNullable<InvestmentTransactionFormInput['fullSecurity']>;
type InvestmentTransactionFormManager = ReturnType<typeof investmentTransactionFormFactory>;

type SubmitTransaction = (
  securityObj: InvestmentSymbolLookupItem | undefined,
  values: InvestmentTransactionFormSubmitPayload,
  existingSecurity?: boolean,
) => void;

type SubmitArgs = {
  payload: InvestmentTransactionFormSubmitPayload;
  manager: InvestmentTransactionFormManager;
};

type EditFormResult = {
  formInput: InvestmentTransactionFormInput;
  onSubmit: (args: SubmitArgs) => void;
  transaction: Partial<TransactionProps> | undefined;
};

const toTransactionProps = (
  values: InvestmentTransactionFormSubmitPayload,
  localSecurityId?: string | null,
): TransactionProps => {
  const { fullSecurity: _fullSecurity, ...transactionValues } = values;
  return {
    ...transactionValues,
    amount: values.amount,
    postedOn: values.postedOn,
    localSecurityId: localSecurityId ?? values.localSecurityId,
    type: values.type ?? 'INVESTMENT',
  };
};

export function useInvestmentTransactionEditForm(props: InvestmentTransactionEditDialogProps): EditFormResult {
  const { context, transaction } = props;
  const dispatch = useAppDispatch();
  const { saveTransactions } = useTxnActions({ wrapperId: context });
  const submitTransactionRef = useRef<SubmitTransaction | null>(null);
  const transactionPlain = useMemo(
    () => (transaction ? (toPlainIfImmutable(transaction) as Partial<TransactionProps>) : undefined),
    [transaction],
  );

  const formInput = useMemo<InvestmentTransactionFormInput>(
    () => ({
      amount: 0,
      type: 'INVESTMENT',
      ...transactionPlain,
      fullSecurity: undefined,
    }),
    [transactionPlain],
  );

  const closeForm = useCallback(() => {
    if (props.dialogId) {
      dispatch(rootUiActions.removeDialog(props.dialogId));
    }
  }, [dispatch, props.dialogId]);

  submitTransactionRef.current = (securityObj, values, existingSecurity) => {
    const investmentTxnType = values.investmentTxnType || (values.type === 'CASH_FLOW' ? 'VIRTUAL_CASH_FLOW' : '');
    const localSecurityId = existingSecurity ? values.localSecurityId : securityObj?.security?.id;
    const transactionValues = toTransactionProps(values, localSecurityId);

    if (investmentTxnType === 'VIRTUAL_CASH_FLOW') {
      saveTransactions([
        transactionsTypes.mkCashFlowTransaction({
          clientId: uuidv4().toUpperCase(),
          ...transactionValues,
          investmentTxnType: undefined,
          type: 'CASH_FLOW',
        }),
      ]);
    } else {
      const payload = new transactionsTypes.InvestmentTransaction({
        clientId: uuidv4().toUpperCase(),
        ...transactionsUtils.normalizeInvestmentTransaction(transactionValues),
        localSecurityId,
      });

      trackEventTTI(tracker.events.txnSave, transactionsSelectors.getTrackingProps(undefined, payload));

      if (values.id) {
        dispatch(transactionsActions.updateTransaction(payload, { undo: { userMessage: 'Transaction updated.' } }));
      } else {
        dispatch(transactionsActions.createTransaction(payload, { undo: { userMessage: 'Transaction created.' } }));
      }
    }

    closeForm();
  };

  const onSubmit = useCallback(
    ({ payload }: SubmitArgs) => {
      if (payload.fullSecurity && !payload.fullSecurity.security?.id && payload.fullSecurity.name) {
        securitiesUtils.createNewSecurityAndSendPackage(
          payload.fullSecurity,
          dispatch,
          assert,
          (securityObj, values) => submitTransactionRef.current?.(securityObj, values),
          payload,
        );
        return;
      }
      submitTransactionRef.current?.(undefined, payload, true);
    },
    [dispatch],
  );

  return { formInput, onSubmit, transaction: transactionPlain };
}
